// Platform-agnostic auth types, shared by the native (index.ts) and web
// (index.web.ts) implementations so callers (UI, hooks) don't care which
// Firebase SDK is underneath.

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  providerId: string; // 'password' | 'google.com' | 'apple.com' | ...
}

export type AuthErrorCode =
  | 'invalid-email'
  | 'invalid-credential'
  | 'user-not-found'
  | 'wrong-password'
  | 'email-already-in-use'
  | 'weak-password'
  | 'requires-recent-login'
  | 'too-many-requests'
  | 'network'
  | 'not-configured'
  | 'unknown';

export class AuthError extends Error {
  code: AuthErrorCode;
  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AuthError';
    this.code = code;
  }
}

// Firebase (both the JS SDK and react-native-firebase) report `auth/*` codes;
// collapse them into our small union so the UI can show a localized message.
const ERROR_MAP: Record<string, AuthErrorCode> = {
  'auth/invalid-email': 'invalid-email',
  'auth/invalid-credential': 'invalid-credential',
  'auth/user-not-found': 'user-not-found',
  'auth/wrong-password': 'wrong-password',
  'auth/email-already-in-use': 'email-already-in-use',
  'auth/weak-password': 'weak-password',
  'auth/requires-recent-login': 'requires-recent-login',
  'auth/too-many-requests': 'too-many-requests',
  'auth/network-request-failed': 'network',
};

export const mapAuthError = (e: unknown): AuthError => {
  if (e instanceof AuthError) return e;
  const code =
    typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string'
      ? (e as { code: string }).code
      : '';
  const message =
    typeof e === 'object' && e !== null ? (e as { message?: string }).message : undefined;
  return new AuthError(ERROR_MAP[code] ?? 'unknown', message);
};

export interface AuthService {
  isAvailable: () => boolean;
  getCurrentUser: () => AuthUser | null;
  onAuthChanged: (cb: (user: AuthUser | null) => void) => () => void;
  signUpWithEmail: (email: string, password: string) => Promise<AuthUser>;
  signInWithEmail: (email: string, password: string) => Promise<AuthUser>;
  signInWithGoogle: () => Promise<AuthUser>;
  signInWithApple: () => Promise<AuthUser>;
  signInAnonymously: () => Promise<AuthUser>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

export default {};
